import React, { useState } from 'react';
import { useEditMode } from '../hooks/useEditMode';
import { db } from '../lib/firebase';
import { doc, setDoc } from 'firebase/firestore';
import { Save, X, Edit2 } from 'lucide-react';

interface EditableContentProps {
  section: string;
  contentKey: string;
  defaultValue: string;
  type?: 'text' | 'textarea' | 'html';
  className?: string;
  onSave?: (value: string) => Promise<void> | void;
}

export const EditableContent: React.FC<EditableContentProps> = ({
  section,
  contentKey,
  defaultValue,
  type = 'text',
  className = '',
  onSave
}) => {
  const { isEditMode } = useEditMode();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(defaultValue);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (onSave) {
        await onSave(value);
      } else {
        const docRef = doc(db, 'content', `${section}_${contentKey}`);
        await setDoc(docRef, {
          section,
          key: contentKey,
          value: { content: value },
          updatedAt: new Date()
        }, { merge: true });
      }

      setIsEditing(false);
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isEditMode) {
    return <span className={className}>{value}</span>;
  }

  if (!isEditing) {
    return (
      <button
        onClick={() => setIsEditing(true)}
        className={`group relative inline-flex items-center gap-2 px-2 py-1 rounded hover:bg-dark/5 text-left w-full ${className}`}
        title="Cliquer pour modifier"
      >
        <span>{value}</span>
        <Edit2 className="h-4 w-4 text-dark/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
      </button>
    );
  }

  return (
    <div className="relative">
      {type === 'textarea' ? (
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-full p-2 border border-dark/20 rounded-md focus:border-dark focus:ring-1 focus:ring-dark text-left"
          rows={5}
          autoFocus
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-full p-2 border border-dark/20 rounded-md focus:border-dark focus:ring-1 focus:ring-dark text-left"
          autoFocus
        />
      )}
      <div className="absolute right-0 mt-2 flex gap-2">
        <button
          onClick={() => {
            setValue(defaultValue);
            setIsEditing(false);
          }}
          className="p-1.5 text-red-600/60 hover:text-red-600 hover:bg-red-50 rounded-md"
          disabled={isSaving}
          title="Annuler"
        >
          <X className="h-4 w-4" />
        </button>
        <button
          onClick={handleSave}
          className="p-1.5 text-green-600/60 hover:text-green-600 hover:bg-green-50 rounded-md"
          disabled={isSaving}
          title="Enregistrer"
        >
          <Save className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};